import type { QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import type { MyRoomsResponse, RoomMembership } from '../../shared/api';
import { QueryHttpError } from '../api/http';
import { readMyRooms } from '../api/rooms';
import { LIST_GC_TIME, LIST_STALE_TIME } from './leaderboard';

type MyRoomsKey = readonly ['my-rooms', number];

function myRoomsQueryKey(profileGeneration: number): MyRoomsKey {
  return ['my-rooms', profileGeneration];
}

export function myRoomsQueryOptions(
  credential: string | null,
  profileGeneration: number,
): QueryObserverOptions<MyRoomsResponse, QueryHttpError, MyRoomsResponse, MyRoomsResponse, MyRoomsKey> {
  const requestCredential = credential;
  return {
    queryKey: myRoomsQueryKey(profileGeneration),
    queryFn: ({ signal }) => {
      if (!requestCredential) throw new QueryHttpError('Your rooms are unavailable.', 401);
      return readMyRooms(requestCredential, signal);
    },
    enabled: Boolean(requestCredential),
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
  };
}

/** Rooms owing a turn first, then most recently active. */
export function orderedRooms(data: MyRoomsResponse | undefined): RoomMembership[] {
  return [...(data?.rooms ?? [])].sort(
    (a, b) => Number(b.needsAction) - Number(a.needsAction) || b.updatedAt - a.updatedAt,
  );
}

export function refreshMyRooms(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: ['my-rooms'] });
}
